import React, { useState } from 'react';
import { css } from '@emotion/react';
import { useTheme } from '@emotion/react';
import type { AppTheme } from '@/theme/theme';
import { useTranslation } from 'react-i18next';
import { i18nKeys } from '@/i18n/i18nKeyGen';
import { Button, ModalShell } from '@/components/ui';
import { useNotificationStore } from '@/store/notificationStore';
import type { CompanyManagerContact } from '@packages/types/auth/dto';

interface SiteNotAssignedModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyManager: CompanyManagerContact | null;
}

export const SiteNotAssignedModal: React.FC<SiteNotAssignedModalProps> = ({
  isOpen,
  onClose,
  companyManager,
}) => {
  const theme = useTheme() as AppTheme;
  const { t } = useTranslation();
  const { showSuccess } = useNotificationStore();
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [copiedPhone, setCopiedPhone] = useState(false);

  const handleCopyEmail = async () => {
    if (!companyManager?.email) return;
    try {
      await navigator.clipboard.writeText(companyManager.email);
      setCopiedEmail(true);
      showSuccess('auth:email_copied', 'auth:email_copied_message');
      setTimeout(() => setCopiedEmail(false), 2000);
    } catch (err) {
      console.error('Failed to copy email:', err);
    }
  };

  const handleCopyPhone = async () => {
    if (!companyManager?.phone) return;
    try {
      await navigator.clipboard.writeText(companyManager.phone);
      setCopiedPhone(true);
      showSuccess('auth:phone_copied', 'auth:phone_copied_message');
      setTimeout(() => setCopiedPhone(false), 2000);
    } catch (err) {
      console.error('Failed to copy phone:', err);
    }
  };

  return (
    <ModalShell
      isOpen={isOpen}
      title={t(i18nKeys.auth.site_not_assigned)}
      onClose={onClose}
      size="md"
      showCloseButton={false}
      footer={
        <Button onClick={onClose} variant="primary" width="100%">
          {t(i18nKeys.auth.close)}
        </Button>
      }
    >
      <p css={styles.message(theme)}>{t(i18nKeys.auth.site_not_assigned_message)}</p>

      {companyManager && (
        <div css={styles.contactInfo}>
          <h3 css={styles.contactTitle(theme)}>{t(i18nKeys.auth.contact_company_manager)}</h3>

          <div css={styles.infoCard(theme)}>
            <div css={styles.infoRow}>
              <div css={styles.infoLabel(theme)}>{t(i18nKeys.auth.company_manager)}:</div>
              <div css={styles.infoValue(theme)}>{companyManager.name}</div>
            </div>

            <div css={styles.infoRow}>
              <div css={styles.infoLabel(theme)}>{t(i18nKeys.auth.email)}:</div>
              <div css={styles.emailContainer}>
                <a
                  href={`mailto:${companyManager.email}`}
                  css={styles.emailLink(theme)}
                  onClick={e => {
                    e.preventDefault();
                    handleCopyEmail();
                  }}
                >
                  {companyManager.email}
                </a>
                <Button
                  onClick={handleCopyEmail}
                  variant={copiedEmail ? 'primary' : 'tertiary'}
                  aria-label={t(i18nKeys.auth.copy)}
                  css={styles.copyButton(theme, copiedEmail)}
                  title={t(i18nKeys.auth.copy)}
                >
                  {copiedEmail ? (
                    <svg
                      width="16"
                      height="16"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  ) : (
                    <svg
                      width="16"
                      height="16"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                  )}
                </Button>
              </div>
            </div>

            {companyManager.phone && (
              <div css={styles.infoRow}>
                <div css={styles.infoLabel(theme)}>{t(i18nKeys.auth.phone)}:</div>
                <div css={styles.phoneContainer}>
                  <a
                    href={`tel:${companyManager.phone}`}
                    css={styles.phoneLink(theme)}
                    onClick={e => {
                      e.preventDefault();
                      handleCopyPhone();
                    }}
                  >
                    {companyManager.phone}
                  </a>
                  <Button
                    onClick={handleCopyPhone}
                    variant={copiedPhone ? 'primary' : 'tertiary'}
                    aria-label={t(i18nKeys.auth.copy)}
                    css={styles.copyButton(theme, copiedPhone)}
                    title={t(i18nKeys.auth.copy)}
                  >
                    {copiedPhone ? (
                      <svg
                        width="16"
                        height="16"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    ) : (
                      <svg
                        width="16"
                        height="16"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </ModalShell>
  );
};

const styles = {
  message: (theme: AppTheme) => css`
    margin: 0 0 24px;
    font-size: ${theme.fontSize.md};
    line-height: 1.6;
    color: ${theme.colors.text.secondary};
  `,

  contactInfo: css`
    margin-top: 24px;
  `,

  contactTitle: (theme: AppTheme) => css`
    margin: 0 0 16px;
    font-size: ${theme.fontSize.lg};
    font-weight: ${theme.fontWeight.semibold};
    color: ${theme.colors.text.primary};
  `,

  infoCard: (theme: AppTheme) => css`
    background: ${theme.colors.surface.layout.overlay};
    border: 1px solid ${theme.colors.border.container};
    border-radius: ${theme.borderRadius.md};
    padding: 16px;
  `,

  infoRow: css`
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 12px;

    &:last-child {
      margin-bottom: 0;
    }
  `,

  infoLabel: (theme: AppTheme) => css`
    font-size: ${theme.fontSize.xs};
    font-weight: ${theme.fontWeight.medium};
    color: ${theme.colors.text.secondary};
    text-transform: uppercase;
    letter-spacing: 0.5px;
  `,

  infoValue: (theme: AppTheme) => css`
    font-size: ${theme.fontSize.md};
    color: ${theme.colors.text.primary};
    font-weight: ${theme.fontWeight.medium};
  `,

  emailContainer: css`
    display: flex;
    align-items: center;
    gap: 8px;
  `,

  phoneContainer: css`
    display: flex;
    align-items: center;
    gap: 8px;
  `,

  emailLink: (theme: AppTheme) => css`
    color: ${theme.colors.button.bg.primary.default};
    text-decoration: none;
    transition: color 0.2s;
    flex: 1;
    cursor: pointer;

    &:hover {
      color: ${theme.colors.button.bg.primary.hover};
      text-decoration: underline;
    }
  `,

  phoneLink: (theme: AppTheme) => css`
    color: ${theme.colors.button.bg.primary.default};
    text-decoration: none;
    transition: color 0.2s;
    flex: 1;
    cursor: pointer;

    &:hover {
      color: ${theme.colors.button.bg.primary.hover};
      text-decoration: underline;
    }
  `,

  copyButton: (theme: AppTheme, copied: boolean) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: 1px solid
      ${copied ? theme.colors.status.stable.stroke.border : theme.colors.border.container};
    background: ${copied
      ? theme.colors.surface.feedback.success
      : theme.colors.surface.control.default};
    color: ${copied ? 'white' : theme.colors.text.secondary};
    border-radius: ${theme.borderRadius.sm};
    cursor: pointer;
    transition: all 0.2s;
    padding: 0;

    &:hover {
      background: ${copied
        ? theme.colors.surface.feedback.success
        : theme.colors.surface.interactive.hover};
      border-color: ${copied
        ? theme.colors.status.stable.stroke.border
        : theme.colors.button.bg.primary.default};
      color: ${copied ? 'white' : theme.colors.button.bg.primary.default};
    }

    &:active {
      transform: scale(0.95);
    }

    svg {
      width: 16px;
      height: 16px;
    }
  `,
};
